import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Check, X, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import {
  getLeaveTypes,
  createLeaveType,
  updateLeaveType,
  getPendingLeave,
  decideLeaveRequest
} from '../../services/api/implementation/impl';
import {
  Callout
} from '../../../shared/components/ui/primitives';

const TABS = [
  { key: 'requests', label: 'Requests' },
  { key: 'types', label: 'Leave types' }
];

const ACCRUALS = [
  { value: 'MONTHLY', label: 'Monthly', hint: 'A twelfth of the quota each month' },
  { value: 'YEARLY', label: 'Yearly', hint: 'The whole quota on 1 January' },
  { value: 'NONE', label: 'None', hint: 'Managed by hand — used for unpaid leave' }
];

/**
 * Leave configuration and approvals.
 *
 * Nothing is seeded, so the types tab starts empty on purpose — a default
 * CL/SL/EL set with quotas nobody reviewed would be worse than a blank list
 * that obviously needs filling in. The empty state says so rather than looking
 * broken.
 */
const LeaveManagement = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=employees' });
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('requests');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const types = useQuery({
    queryKey: ['leaveTypes'],
    queryFn: () => getLeaveTypes(true)
  });

  const pending = useQuery({
    queryKey: ['pendingLeave'],
    queryFn: getPendingLeave
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }) => decideLeaveRequest(id, approve),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pendingLeave'] });
      toast.success(variables.approve ? 'Leave approved' : 'Leave rejected');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  const typeRows = types.data ?? [];
  const requests = pending.data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Leave' }} />

      <div className="p-4 space-y-4">
        <div className="flex gap-2 border-b border-gray-200">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.key
                  ? 'border-brand-700 text-brand-800'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
              {t.key === 'requests' && requests.length > 0 && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full bg-warning-bg text-warning-fg text-xs">
                  {requests.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {tab === 'requests' ? (
          pending.isFetching ? (
            <Loader />
          ) : requests.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
              No leave requests waiting on you.
            </div>
          ) : (
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <Th>Employee</Th>
                    <Th>Type</Th>
                    <Th>Dates</Th>
                    <Th>Days</Th>
                    <Th>Balance</Th>
                    <Th>Reason</Th>
                    <Th>Decision</Th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((row) => {
                    // Approving beyond the balance is refused server-side, so
                    // the button is disabled with the reason rather than left
                    // enabled to fail.
                    const short = Number(row.balance_available) < Number(row.days);
                    return (
                      <tr key={row.id} className="border-t border-gray-100">
                        <Td>
                          <div className="font-medium text-gray-900">
                            {row.user?.name ?? `#${row.user_id}`}
                          </div>
                        </Td>
                        <Td>{row.leaveType?.name ?? '—'}</Td>
                        <Td>
                          {formatDate(row.from_date)}
                          {row.from_date !== row.to_date && (
                            <> → {formatDate(row.to_date)}</>
                          )}
                        </Td>
                        <Td>{formatDays(row.days)}</Td>
                        <Td className={short ? 'text-danger-fg font-medium' : ''}>
                          {formatDays(row.balance_available)}
                          {short && (
                            <div className="text-xs">not enough</div>
                          )}
                        </Td>
                        <Td className="text-gray-600 text-xs max-w-xs">
                          {row.reason ?? '—'}
                        </Td>
                        <Td>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={decide.isPending || short}
                              title={
                                short
                                  ? 'Not enough balance — this would be refused'
                                  : undefined
                              }
                              onClick={() =>
                                decide.mutate({ id: row.id, approve: true })
                              }
                              className="flex items-center gap-1 px-3 py-1.5 rounded bg-success text-white text-xs font-medium disabled:opacity-50"
                            >
                              <Check size={13} /> Approve
                            </button>
                            <button
                              type="button"
                              disabled={decide.isPending}
                              onClick={() =>
                                decide.mutate({ id: row.id, approve: false })
                              }
                              className="flex items-center gap-1 px-3 py-1.5 rounded border border-danger-border text-danger-fg text-xs font-medium disabled:opacity-50"
                            >
                              <X size={13} /> Reject
                            </button>
                          </div>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : (
          <>
            <Callout tone="info" collapsible icon={<Info size={14} />} title="How accrual works">
              Monthly accrual credits a twelfth of the quota on the 1st, rounded to
              the nearest half day. Balances open each January carrying forward up
              to the cap set here.
            </Callout>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setCreating(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-700 text-white text-sm font-medium"
              >
                <Plus size={16} /> Add leave type
              </button>
            </div>

            {types.isFetching ? (
              <Loader />
            ) : typeRows.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg py-10 text-center">
                <p className="text-gray-600 font-medium">
                  No leave types configured yet.
                </p>
                <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
                  Nothing is set up by default, because quotas and carry-forward
                  are policy decisions. Until at least one type exists, nobody
                  can apply for leave.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <Th>Name</Th>
                      <Th>Code</Th>
                      <Th>Annual quota</Th>
                      <Th>Accrual</Th>
                      <Th>Carry forward</Th>
                      <Th>Paid</Th>
                      <Th>Active</Th>
                      <Th />
                    </tr>
                  </thead>
                  <tbody>
                    {typeRows.map((type) => (
                      <tr key={type.id} className="border-t border-gray-100">
                        <Td className="font-medium text-gray-900">{type.name}</Td>
                        <Td>{type.code}</Td>
                        <Td>{formatDays(type.annual_quota)}</Td>
                        <Td>{titleCase(type.accrual)}</Td>
                        <Td>{formatDays(type.carry_forward_max)}</Td>
                        <Td>
                          {type.is_paid ? (
                            'Paid'
                          ) : (
                            <span className="text-warning-fg">Unpaid</span>
                          )}
                        </Td>
                        <Td>
                          {type.is_active ? (
                            <span className="text-success-fg">Active</span>
                          ) : (
                            <span className="text-gray-400">Inactive</span>
                          )}
                        </Td>
                        <Td>
                          <button
                            type="button"
                            onClick={() => setEditing(type)}
                            className="text-brand-700 text-xs underline"
                          >
                            Edit
                          </button>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {(creating || editing) && (
        <LeaveTypeModal
          type={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['leaveTypes'] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </Layout>
  );
};

const LeaveTypeModal = ({ type, onClose, onSaved }) => {
  const isEdit = Boolean(type);
  const [form, setForm] = useState(() => ({
    name: type?.name ?? '',
    code: type?.code ?? '',
    annual_quota: type?.annual_quota ?? 12,
    accrual: type?.accrual ?? 'MONTHLY',
    carry_forward_max: type?.carry_forward_max ?? 0,
    is_paid: type?.is_paid ?? true,
    allow_negative: type?.allow_negative ?? false,
    is_active: type?.is_active ?? true
  }));

  const save = useMutation({
    mutationFn: (payload) =>
      isEdit ? updateLeaveType(type.id, payload) : createLeaveType(payload),
    onSuccess: () => {
      toast.success(isEdit ? 'Leave type updated' : 'Leave type created');
      onSaved();
    },
    onError: (err) => toast.error(err?.response?.data?.message || 'Could not save')
  });

  const set = (key) => (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [key]: value });
  };

  const submit = () => {
    if (!form.name.trim() || !form.code.trim()) {
      toast.error('Name and code are both required');
      return;
    }
    save.mutate({
      ...form,
      annual_quota: Number(form.annual_quota),
      carry_forward_max: Number(form.carry_forward_max)
    });
  };

  const accrualHint = ACCRUALS.find((a) => a.value === form.accrual)?.hint;

  return (
    <Modal
      name={isEdit ? `Edit ${type.name}` : 'Add leave type'}
      onClose={onClose}
      width="480px"
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name">
            <input
              type="text"
              value={form.name}
              onChange={set('name')}
              placeholder="Casual Leave"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Code">
            <input
              type="text"
              value={form.code}
              onChange={set('code')}
              disabled={isEdit}
              placeholder="CL"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            />
            {isEdit && (
              <p className="text-xs text-gray-500 mt-1">
                Codes cannot change — approved requests and exports already refer
                to this one.
              </p>
            )}
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Annual quota (days)">
            <input
              type="number"
              step="0.5"
              value={form.annual_quota}
              onChange={set('annual_quota')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
          <Field label="Carry forward max">
            <input
              type="number"
              step="0.5"
              value={form.carry_forward_max}
              onChange={set('carry_forward_max')}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </Field>
        </div>

        <Field label="Accrual">
          <select
            value={form.accrual}
            onChange={set('accrual')}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            {ACCRUALS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
          {accrualHint && (
            <p className="text-xs text-gray-500 mt-1">{accrualHint}</p>
          )}
        </Field>

        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input type="checkbox" checked={form.is_paid} onChange={set('is_paid')} />
          Paid leave
        </label>

        <label className="flex items-start gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={form.allow_negative}
            onChange={set('allow_negative')}
            className="mt-1"
          />
          <span>
            Allow the balance to go negative
            <span className="block text-xs text-gray-500">
              Normally off — the point of a quota is that it runs out. Turn on for
              unpaid leave, which has no quota to exhaust.
            </span>
          </span>
        </label>

        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={set('is_active')}
            />
            Active
          </label>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded border border-gray-300 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={save.isPending}
            onClick={submit}
            className="px-4 py-2 rounded bg-brand-700 text-white text-sm disabled:opacity-50"
          >
            {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create type'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    {children}
  </div>
);

function formatDays(value) {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return '0';
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function titleCase(value) {
  if (!value) return '—';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: '2-digit'
  });
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-4 py-3 align-top ${className}`}>{children}</td>
);

export default LeaveManagement;
